import { endOfDay, startOfDay, subDays } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import type { DateRange } from "react-day-picker";

import {
  type CompleteDateRange,
  createStructuredSearchDefaultDateRange,
  isCompleteDateRange,
} from "../stores/structured-search/structured-search-store";

import { DatePickerWithRange } from "@/components/DatePickerWithRange";

const buildSearchBarPresets = (): Record<string, { from: Date; to: Date }> => {
  const now = new Date();
  return {
    "Last 5 min": { from: new Date(now.getTime() - 5 * 60 * 1000), to: now },
    "Last 15 min": { from: new Date(now.getTime() - 15 * 60 * 1000), to: now },
    "Last 1 hour": { from: new Date(now.getTime() - 60 * 60 * 1000), to: now },
    "Last 6 hours": { from: new Date(now.getTime() - 6 * 60 * 60 * 1000), to: now },
    "Last 24 hours": { from: new Date(now.getTime() - 24 * 60 * 60 * 1000), to: now },
    "Last 7 days": { from: startOfDay(subDays(now, 6)), to: endOfDay(now) },
    "Last 14 days": { from: startOfDay(subDays(now, 13)), to: endOfDay(now) },
    "Last 30 days": { from: startOfDay(subDays(now, 29)), to: endOfDay(now) },
  };
};

const isDefaultStructuredDateRange = (dateRange: CompleteDateRange): boolean => {
  const defaultDateRange = createStructuredSearchDefaultDateRange();
  return (
    dateRange.from.getTime() === defaultDateRange.from.getTime() &&
    dateRange.to.getTime() === defaultDateRange.to.getTime()
  );
};

interface SearchBarDateRangePickerProps {
  dateRange: CompleteDateRange;
  minDate?: Date;
  maxDate?: Date;
  variant?: "default" | "tinyIcon";
  className?: string;
  triggerClassName?: string;
  onDateRangeChange: (range: CompleteDateRange) => void;
}

export const SearchBarDateRangePicker = ({
  dateRange,
  minDate,
  maxDate,
  variant = "default",
  className,
  triggerClassName,
  onDateRangeChange,
}: SearchBarDateRangePickerProps) => {
  const [draftRange, setDraftRange] = useState<DateRange | undefined>(dateRange);
  const [isOpen, setIsOpen] = useState(false);
  const [customPresets, setCustomPresets] = useState(() => buildSearchBarPresets());

  useEffect(() => {
    setDraftRange(dateRange);
  }, [dateRange]);

  useEffect(() => {
    if (isOpen) {
      setCustomPresets(buildSearchBarPresets());
    }
  }, [isOpen]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (!open) {
        setDraftRange(dateRange);
      }
    },
    [dateRange],
  );

  const handleDraftChange = useCallback(
    (nextRange: DateRange | undefined) => {
      setDraftRange(nextRange);
      if (isCompleteDateRange(nextRange)) {
        onDateRangeChange(nextRange);
      }
    },
    [onDateRangeChange],
  );

  return (
    <DatePickerWithRange
      date={draftRange}
      setDate={handleDraftChange}
      datetime
      includeSeconds
      customPresets={customPresets}
      minDate={minDate}
      maxDate={maxDate}
      removable={false}
      hidePresets={false}
      open={isOpen}
      onOpenChange={handleOpenChange}
      triggerVariant={variant}
      className={className}
      triggerClassName={triggerClassName}
      showTriggerIndicator={variant === "tinyIcon" && !isDefaultStructuredDateRange(dateRange)}
    />
  );
};
